import type {
  NewsDeliveryOption,
  NewsJobMode,
  NewsJobResponse,
  NewsJobStatusData,
  NewsNotificationType,
  NewsReadingProfile,
} from "./api";
import {
  clearPendingJobNotifications,
  consumePendingJobNotification,
  readPendingJobNotification,
  type NewsJobNotification,
  writePendingJobNotification,
} from "./jobNotifications";

export type PendingNewsJobSnapshot = {
  id: string;
  jobId: string;
  keyword: string;
  notificationType: NewsNotificationType;
  acceptedAt?: string | null;
  startedAt: string;
  mode?: NewsJobMode | null;
  readingProfile?: NewsReadingProfile | null;
  deliveryOption?: NewsDeliveryOption | null;
  callbackMessage?: string | null;
  summary?: string | null;
};

export type PendingNewsJobNotification = Omit<NewsJobNotification, "kind">;

const NEWS_PENDING_JOB_STORAGE_KEY = "wizmate.news.pending-job.v1";
const NEWS_JOB_MODE_HISTORY_STORAGE_KEY = "wizmate.news.job-mode-history.v1";
export const NEWS_PENDING_JOB_EVENT = "wizmate:news-pending-job";

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function hasWindow() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function dispatchPendingNewsJobEvent(snapshot: PendingNewsJobSnapshot | null) {
  if (!hasWindow()) return;

  window.dispatchEvent(
    new CustomEvent<PendingNewsJobSnapshot | null>(NEWS_PENDING_JOB_EVENT, {
      detail: snapshot,
    })
  );
}

function readNewsJobModeHistory(): Record<string, NewsJobMode> {
  if (!hasWindow()) return {};

  const raw = window.localStorage.getItem(NEWS_JOB_MODE_HISTORY_STORAGE_KEY);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};

    const entries = Object.entries(parsed as Record<string, unknown>).filter(
      ([, value]) => value === "news" || value === "report"
    ) as Array<[string, NewsJobMode]>;

    return Object.fromEntries(entries);
  } catch {
    return {};
  }
}

function writeNewsJobModeHistory(history: Record<string, NewsJobMode>) {
  if (!hasWindow()) return;
  window.localStorage.setItem(NEWS_JOB_MODE_HISTORY_STORAGE_KEY, JSON.stringify(history));
}

export function rememberNewsJobMode(jobId: string, mode?: NewsJobMode | null) {
  if (!hasWindow()) return;
  const normalizedJobId = normalize(jobId);
  if (!normalizedJobId || !mode) return;

  const history = readNewsJobModeHistory();
  history[normalizedJobId] = mode;

  const keys = Object.keys(history);
  if (keys.length > 200) {
    const pruned = keys.slice(keys.length - 200);
    writeNewsJobModeHistory(
      Object.fromEntries(pruned.map((key) => [key, history[key]] as const))
    );
    return;
  }

  writeNewsJobModeHistory(history);
}

export function resolveRememberedNewsJobMode(jobId?: string | null): NewsJobMode | undefined {
  const normalizedJobId = normalize(jobId);
  if (!normalizedJobId) return undefined;

  return readNewsJobModeHistory()[normalizedJobId];
}

function toStoredNewsJobNotification(
  notification: PendingNewsJobNotification
): NewsJobNotification {
  return {
    kind: "news",
    jobId: notification.jobId,
    status: notification.status,
    processedAt: notification.processedAt,
    keyword: notification.keyword,
    notificationType: notification.notificationType,
    mode: notification.mode,
    failedStage: notification.failedStage,
    failedError: notification.failedError,
    callbackMessage: notification.callbackMessage,
    summary: notification.summary,
  };
}

function fromStoredNewsJobNotification(
  notification: NewsJobNotification | null
): PendingNewsJobNotification | null {
  if (!notification) return null;

  return {
    jobId: notification.jobId,
    status: notification.status,
    processedAt: notification.processedAt,
    keyword: notification.keyword,
    notificationType: notification.notificationType,
    mode: notification.mode,
    failedStage: notification.failedStage,
    failedError: notification.failedError,
    callbackMessage: notification.callbackMessage,
    summary: notification.summary,
  };
}

export function createPendingNewsJobSnapshot(response: NewsJobResponse): PendingNewsJobSnapshot {
  const startedAt = normalize(response.acceptedAt) ?? new Date().toISOString();

  return {
    id: response.id,
    jobId: response.jobId,
    keyword: response.keyword,
    notificationType: response.notificationType,
    acceptedAt: response.acceptedAt ?? null,
    startedAt,
  };
}

export function readPendingNewsJobSnapshot(): PendingNewsJobSnapshot | null {
  if (!hasWindow()) return null;

  const raw = window.localStorage.getItem(NEWS_PENDING_JOB_STORAGE_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    const id = normalize(parsed?.id);
    const jobId = normalize(parsed?.jobId);
    const keyword = normalize(parsed?.keyword);
    const startedAt = normalize(parsed?.startedAt);
    const notificationType =
      parsed?.notificationType === "line" ||
      parsed?.notificationType === "google_chat" ||
      parsed?.notificationType === "email"
        ? parsed.notificationType
        : undefined;

    if (!id || !jobId || !keyword || !startedAt || !notificationType) {
      return null;
    }

    const mode =
      parsed?.mode === "news" || parsed?.mode === "report" ? parsed.mode : undefined;
    const readingProfile =
      parsed?.readingProfile === "grasp" ||
      parsed?.readingProfile === "organize" ||
      parsed?.readingProfile === "think"
        ? parsed.readingProfile
        : undefined;
    const deliveryOption =
      parsed?.deliveryOption === "text" ||
      parsed?.deliveryOption === "audio" ||
      parsed?.deliveryOption === "text_and_audio"
        ? parsed.deliveryOption
        : undefined;

    return {
      id,
      jobId,
      keyword,
      notificationType,
      acceptedAt: normalize(parsed?.acceptedAt) ?? null,
      startedAt,
      mode: mode ?? null,
      readingProfile: readingProfile ?? null,
      deliveryOption: deliveryOption ?? null,
      callbackMessage: normalize(parsed?.callbackMessage) ?? null,
      summary: normalize(parsed?.summary) ?? null,
    };
  } catch {
    return null;
  }
}

export function writePendingNewsJobSnapshot(snapshot: PendingNewsJobSnapshot) {
  if (!hasWindow()) return;

  rememberNewsJobMode(snapshot.jobId, snapshot.mode);

  window.localStorage.setItem(
    NEWS_PENDING_JOB_STORAGE_KEY,
    JSON.stringify({
      id: snapshot.id,
      jobId: snapshot.jobId,
      keyword: snapshot.keyword,
      notificationType: snapshot.notificationType,
      acceptedAt: snapshot.acceptedAt,
      startedAt: snapshot.startedAt,
      mode: snapshot.mode,
      readingProfile: snapshot.readingProfile,
      deliveryOption: snapshot.deliveryOption,
      callbackMessage: snapshot.callbackMessage,
      summary: snapshot.summary,
    })
  );
  dispatchPendingNewsJobEvent(snapshot);
}

export function clearPendingNewsJobSnapshot() {
  if (!hasWindow()) return;
  window.localStorage.removeItem(NEWS_PENDING_JOB_STORAGE_KEY);
  dispatchPendingNewsJobEvent(null);
}

export function readPendingNewsJobNotification() {
  return fromStoredNewsJobNotification(
    readPendingJobNotification("news") as NewsJobNotification | null
  );
}

export function writePendingNewsJobNotification(notification: PendingNewsJobNotification) {
  // 同種の通知は1件のジョブしか同時に追跡しないため、processing書き込み時も
  // 必ず既存の同種通知をクリアしてから書き込む（そうしないと過去の未dismiss通知が残存し、
  // 新しいジョブの通知と同時に表示されてしまう）
  clearPendingJobNotifications("news");
  writePendingJobNotification(toStoredNewsJobNotification(notification));
}

export function clearPendingNewsJobNotification() {
  clearPendingJobNotifications("news");
}

export function consumePendingNewsJobNotification() {
  return fromStoredNewsJobNotification(
    consumePendingJobNotification("news") as NewsJobNotification | null
  );
}

export function toPendingNewsJobNotification(
  response: NewsJobStatusData,
  options?: { mode?: NewsJobMode | null }
): PendingNewsJobNotification | null {
  if (response.status !== "completed" && response.status !== "failed") {
    return null;
  }

  return {
    jobId: response.jobId,
    status: response.status,
    processedAt: response.processedAt ?? undefined,
    keyword: response.keyword,
    notificationType: response.notificationType,
    ...(options?.mode ? { mode: options.mode } : {}),
    failedStage: response.failedStage ?? undefined,
    failedError: response.errorMessage ?? undefined,
    callbackMessage: response.callbackMessage ?? undefined,
    summary: response.summary ?? undefined,
  };
}

export function getNewsNotificationTypeLabel(notificationType?: NewsNotificationType | null) {
  if (notificationType === "line") return "LINE";
  if (notificationType === "email") return "メール";
  return "テスト用Chat";
}

export function getDefaultNewsNotificationType(
  canUseLine: boolean,
  canUseEmail: boolean
): NewsNotificationType {
  if (canUseLine) return "line";
  if (canUseEmail) return "email";
  return "google_chat";
}

export function resolveNewsNotificationType(
  notificationType: NewsNotificationType | undefined | null,
  canUseLine: boolean,
  canUseEmail: boolean
): NewsNotificationType {
  if (notificationType === "google_chat") return "google_chat";
  if (notificationType === "email" && canUseEmail) return "email";
  if (notificationType === "line" && canUseLine) return "line";
  return getDefaultNewsNotificationType(canUseLine, canUseEmail);
}

export function formatNewsJobStageLabel(stage?: string | null) {
  const normalized = (stage ?? "").trim();
  return normalized || "処理";
}
